import {ToggleButtonGroup, ToggleButton, OutlinedInput, FormControl, IconButton, InputLabel, TextField, Button, Select, Grid} from '@mui/material';
import {forwardRef, useImperativeHandle, useRef} from 'react'
import ClearIcon from '@mui/icons-material/Clear';
import {useTranslation} from "react-i18next";
import {observer} from 'mobx-react';
import {sexList, accuracyList, ageList, plumageList, stateList} from './Lists.js';
import {FIRST_OBS_STEP, alreadyInState, stateIsValid} from './Util.js';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import StateWiz2 from './StateDialog.js';

const TradStateComp = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	const pPress = (event) => doStateChange(props.id, {"id":2,"state":"p"});
	const mPress = (event) => doStateChange(props.id, {"id":4,"state":"m"});

	function doStateChange(so, value) {
		if (!value || value.id === 1)
			return;

		let add_str = '', valid;

		if (alreadyInState(gStorage.add.obs.subobs[so].sum_desc, value.state))
			return;

		if (gStorage.add.obs.subobs[so].sum_desc.length > 0)
			add_str = ', ';

		add_str += value.state;
		valid = stateIsValid(gStorage.add.obs.subobs[so].sum_desc + add_str, stateList);
		gStorage.add.setSOState(so, gStorage.add.obs.subobs[so].sum_desc + add_str, valid);
	}

	const stateChange = (event) => {
		let num = parseInt(event.target.value, 10); 
		doStateChange(props.id, stateList.find(x => x.id === num));
	}

	function clearStates(e) {
		gStorage.add.setSOState(props.id, '', false);
	}

	if (gStorage.conn.user.statewiz)
		return null;

	return (
		<div>
			<Grid size={{xs: 12}} align={'center'}>
				<Button variant="contained" color="primary" size="small" style={{width: 142, margin: 8}} onClick={pPress}>p</Button>
				<Button variant="contained" color="primary" size="small" style={{width: 142, margin: 8}} onClick={mPress}>m</Button>
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" size="small">
					<InputLabel>{t('add.state')}</InputLabel>
					<Select
						native
						value={1}
						onChange={stateChange}
						style={{width: 300, margin: 4}}>
							{stateList.map((dt, i) => {
								return 	(<option key={dt.id} value={dt.id}>{dt.state} </option>);
							})}
					</Select>
				</FormControl>
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" size="small">
					<div>
						<OutlinedInput 
							size="small"
							readOnly
							value={gStorage.add.obs.subobs[props.id].sum_desc}
							error={!gStorage.add.obs.subobs[props.id].state_valid}
						/>
						<IconButton aria-label="tyhjennä" onClick={clearStates}>
							<ClearIcon />
						</IconButton>
					</div>
				</FormControl>
			</Grid>
		</div>
	);
});

const AddObs = observer(forwardRef((props, ref) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const swRef = useRef();

	/* ------------------------------------------------------------------ */

	function numChange(e) {
		let num = parseInt(e.target.value, 10);

		if (isNaN(num))
			num = 0;

		gStorage.add.setSONum(props.id, num, e.target.value);
	}

	function sexChange(e, value) {
		let num = parseInt(value, 10);
		if (isNaN(num))
			return;

		if (!sexList.find(x => x.id === num))
			return;

		gStorage.add.setSOSex(props.id, num);
	}

	function ageChange(e) {
		let num = parseInt(e.target.value, 10);
		if (isNaN(num))
			return;

		if (!ageList.find(x => x.id === num))
			return;

		gStorage.add.setSOAge(props.id, num);
	}

	function plumageChange(e) {
		let num = parseInt(e.target.value, 10);
		if (isNaN(num))
			return;

		if (!plumageList.find(x => x.id === num))
			return;

		gStorage.add.setSOPlumage(props.id, num);
	}
	
	function memoChange(e) {
		gStorage.add.setSOMemo(props.id, e.target.value);
	}

	function bigMinus(e) {
		let val;

		val = gStorage.add.obs.subobs[props.id].num - 10;
		gStorage.add.setSONum(props.id, val, val);
	}

	function littleMinus(e) {
		let val;

		val = gStorage.add.obs.subobs[props.id].num - 1;
		gStorage.add.setSONum(props.id, val, val);
	}

	function littlePlus(e) {
		let val;

		val = gStorage.add.obs.subobs[props.id].num + 1;
		gStorage.add.setSONum(props.id, val, val);
	}

	function bigPlus(e) {
		let val;

		val = gStorage.add.obs.subobs[props.id].num + 10;
		gStorage.add.setSONum(props.id, val, val);
	}

	function accuracyChange(e) {
		let num = parseInt(e.target.value, 10);
		if (isNaN(num))
			return;

		if (!accuracyList.find(x => x.id === num))
			return;

		gStorage.add.setSOAccuracy(props.id, num);
	}

	function accuracyDesc(acc) {
		let str = acc.acc;

		if (acc.acc_desc.length > 0)
			str += ' (' + acc.acc_desc + ')';

		return str;
	}

	function bigStateChange(state) {
		swRef.current.bigStateChange(null, state);
	}

	function stateChange(state) {
		swRef.current.stateChange({target: {value: state}});
	}

	function setNumber(val) {
		const ev = {target: {value: val}};
		numChange(ev);
	}
	
	function setAge(val) {
		const ev = {target: {value: val}};
		ageChange(ev);
	}

	function addObsRecAction(cmd, rec) {
		switch (cmd) {
		case 'local':
			bigStateChange('p');
			break;
		case 'migrant':
			bigStateChange('m');
			break;
		
		/* äänet */
		case 'call':
			stateChange('ä');
			break;
		case 'call_seen':
			stateChange('än');
			break;
		case 'song':
			stateChange('Ä');
			break;
		case 'song_seen':
			stateChange('Än');
			break;

		case 'exact':
			accuracyChange({target: {value: accuracyList[0].id}})
			break;
		case 'about':
			accuracyChange({target: {value: accuracyList[1].id}})
			break;
		case 'about2':
			accuracyChange({target: {value: accuracyList[2].id}})
			break;
		case 'about3':
			accuracyChange({target: {value: accuracyList[3].id}})
			break;

		case 'neutral':
			sexChange(null, sexList[0].id);
			break;
		case 'male':
			sexChange(null, sexList[1].id);
			break;
		case 'female':
			sexChange(null, sexList[2].id);
			break;

		/* numerot */
		case 'yksi':
			setNumber(1);
			break;
		case 'kaksi':
			setNumber(2);
			break;
		case 'kolme':
			setNumber(3);
			break;
		case 'neljä':
			setNumber(4);
			break;
		case 'viisi':
			setNumber(5);
			break;
		case 'kuusi':
			setNumber(6);
			break;
		case 'seitsemän':
			setNumber(7);
			break;
		case 'kahdeksan':
			setNumber(8);
			break;
		case 'yhdeksän':
			setNumber(9);
			break;
		case 'kymmenen':
			setNumber(10);
			break;

		/* Age */
		case 'noage':
			setAge(ageList[0].id);
			break;

		case '1kv':
			setAge(ageList[1].id);
			break;
		case '+1kv':
			setAge(ageList[2].id);
			break;
		case '2kv':
			setAge(ageList[3].id);
			break;
		case '+2kv':
			setAge(ageList[4].id);
			break;
		case '3kv':
			setAge(ageList[5].id);
			break;
		case '+3kv':
			setAge(ageList[6].id);
			break;

		default:
			if (cmd.length && !isNaN(Number(cmd))) {
				setNumber(Number(cmd));
			}
		}
	}

	useImperativeHandle(ref, () => ({
		addObsRecAction
	}));

	if (gStorage.add.currentStep !== props.id + FIRST_OBS_STEP)
		return null;

	return (
		<div>
			<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
				<Grid size={{xs: 12}} align={'center'}>
					<FormControl variant="outlined" size="small">
						<InputLabel style={{margin: 8}}>{t('add.accuracy')}</InputLabel>
						<Select
							native
							value={gStorage.add.obs.subobs[props.id].acc_id}
							onChange={accuracyChange}
							style={{width: 143, margin: 8, marginBottom: 4}}>
								{accuracyList.map((dt, i) => {
									return 	(<option key={dt.id} value={dt.id}>{accuracyDesc(dt)} </option>);
								})}
						</Select>
					</FormControl>
					<TextField 
						label={t('add.count')}
						variant="outlined" 
						size="small"
						inputProps={{style: { textAlign: 'right' }}}
						onChange={numChange}
						value={gStorage.add.obs.subobs[props.id].num_text}
						error={gStorage.add.obs.subobs[props.id].num <= 0}
						style={{width:143, margin: 8, marginBottom: 4}}
					/>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<Button onClick={() => { littleMinus(); }} style={{margin: 4}}>-</Button>
					<Button onClick={() => { bigMinus(); }} style={{margin: 4}}>--</Button>
					<Button onClick={() => { bigPlus() }} style={{margin: 4}}>++</Button>
					<Button onClick={() => { littlePlus(); }} style={{margin: 4}}>+</Button>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<ToggleButtonGroup
						size="small"
						value={gStorage.add.obs.subobs[props.id].sex_id}
						onChange={sexChange} 
						exclusive
						disabled={!gStorage.add.spec || !gStorage.add.spec.sexing}
						style={{margin: 8, marginTop: 4}}
					>
						{sexList.map((dt, i) => {
							return (
								<ToggleButton key={i} value={dt.id} style={{width: 100}}>
									{dt.sex}
								</ToggleButton>);
						})}
					</ToggleButtonGroup>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<FormControl variant="outlined" size="small">
						<InputLabel>{t('add.age')}</InputLabel>
						<Select
							native
							value={gStorage.add.obs.subobs[props.id].age_id}
							onChange={ageChange}
							style={{width: 143, margin: 8}}>
								{ageList.map((dt, i) => {
									return 	(<option key={dt.id} value={dt.id}>{dt.age} </option>);
								})}
						</Select>
					</FormControl>
					<FormControl variant="outlined" size="small">
						<InputLabel>{t('add.plumage')}</InputLabel>
						<Select
							native
							value={gStorage.add.obs.subobs[props.id].plumage_id}
							onChange={plumageChange}
							style={{width: 143, margin: 8}}>
								{plumageList.map((dt, i) => {
									return 	(<option key={dt.id} value={dt.id}>{dt.plumage} </option>);
								})}
						</Select>
					</FormControl>
				</Grid>

				<TradStateComp id={props.id} />
				<StateWiz2 ref={swRef} id={props.id} />

				<Grid size={{xs: 12}} align={'center'}>
					<TextField 
						label={t('add.additional_info')}
						variant="outlined"
						value={gStorage.add.obs.subobs[props.id].memo}
						onChange={memoChange}
						multiline rows="4"
						size="small"
						style={{width: 300, margin: 4}} 
					/>
				</Grid>
			</Grid>
		</div>
	);
}));

export default AddObs;
